const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});
const MONTH_DAY_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** The local calendar day of a timestamp, as a key rows are grouped by. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** "Today", "Yesterday · Oct 14", "Oct 13" — the day separator's caption. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(iso) === dayKey(now.toISOString())) return "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) {
    return `Yesterday · ${MONTH_DAY.format(d)}`;
  }
  return d.getFullYear() === now.getFullYear()
    ? MONTH_DAY.format(d)
    : MONTH_DAY_YEAR.format(d);
}

/** "18:04" — the Time column. */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "Oct 15, 2026 · 17:58:37" — the Time column's tooltip. */
export function formatExactTime(iso: string): string {
  const d = new Date(iso);
  return (
    `${MONTH_DAY_YEAR.format(d)} · ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

/**
 * Orders two stream positions, newest last. The fraction is compared as text
 * because the server keeps microseconds and a JS Date keeps milliseconds.
 */
export function compareRows(
  a: { created_at: string; id: string },
  b: { created_at: string; id: string },
): number {
  const [aSec, aFrac] = splitTime(a.created_at);
  const [bSec, bFrac] = splitTime(b.created_at);
  if (aSec !== bSec) return aSec - bSec;
  if (aFrac !== bFrac) return aFrac < bFrac ? -1 : 1;
  return a.id === b.id ? 0 : a.id < b.id ? -1 : 1;
}

function splitTime(iso: string): [number, string] {
  const m = /^(.*?:\d\d)(?:\.(\d+))?(Z|[+-]\d\d:\d\d)$/.exec(iso);
  if (!m) return [Date.parse(iso), ""];
  return [Date.parse(m[1] + m[3]), (m[2] ?? "").padEnd(9, "0")];
}

/** A run of message text, marked when it is the search term. */
export interface Segment {
  text: string;
  hit: boolean;
}

/**
 * How far into a message a match may sit before the cell shows a fragment
 * around it instead of the start — roughly what a narrow Message column
 * still shows on one line.
 */
const SNIPPET_LEAD = 48;
/** Characters of context kept before a match that was cut to. */
const SNIPPET_BEFORE = 36;

/**
 * The text a one-line cell shows for a message under a search: the whole body
 * when the first match is near its start, otherwise a fragment opening a little
 * before the match with a leading ellipsis — so the hit is never cut off.
 */
export function searchSnippet(
  body: string,
  q: string,
): { clipped: boolean; segments: Segment[] } {
  const term = q.trim();
  if (!term) return { clipped: false, segments: [{ text: body, hit: false }] };
  // Matched on the original text: lower-casing can change a string's length
  // ("İ" becomes two code units) and would shift every index after it.
  const pattern = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "giu");
  const first = body.search(pattern);
  let start = 0;
  if (first > SNIPPET_LEAD) {
    start = first - SNIPPET_BEFORE;
    const space = body.indexOf(" ", start);
    if (space !== -1 && space < first) start = space + 1;
  }
  const shown = body.slice(start);
  const segments: Segment[] = [];
  let at = 0;
  for (const m of shown.matchAll(pattern)) {
    const i = m.index;
    if (i > at) segments.push({ text: shown.slice(at, i), hit: false });
    segments.push({ text: m[0], hit: true });
    at = i + m[0].length;
  }
  if (at < shown.length) segments.push({ text: shown.slice(at), hit: false });
  return { clipped: start > 0, segments };
}
